/**
 * What has actually been done to a quotation, read from Frappe's own audit trail.
 *
 * ---- where this comes from ----
 *
 * Nothing in Bosun records a revision history. `Quotation.revision` is a single number — the round
 * you are on — and `get_revisions` returns the `revision_log` child table, which holds what a
 * customer ASKED for, not what was done about it, and which nothing writes any more
 * (`useRevisions.ts`'s `useLogRevisionRequest`).
 *
 * Frappe records the rest itself. Every save writes a `Version` row carrying a field-level diff,
 * and 290 of them exist on this bench. Measured 2026-08-09 against `QTN-2026-00004`:
 *
 *     changed:     [["status","Quoted","Draft"], ["revision","1","2"],
 *                   ["net_total","AED 224.25","AED 251.85"], …]
 *     row_changed: [["items", 0, "qaqrisbbb4", [["qty","5","7"], ["amount","AED 69.00","AED 96.60"]]]]
 *     added:       [["items", {item_code: "7904010006", …}], …]
 *     removed:     [["items", {…}], …]
 *
 * So the history is real and complete; it just has to be read. This module turns those rows into
 * something a purchaser can scan.
 *
 * ---- two things it does NOT do ----
 *
 * It does not reformat money. Frappe has already rendered those values in the document's own
 * currency (`"AED 224.25"`), and re-parsing a formatted string to re-format it is how a figure
 * ends up labelled with the wrong currency — this app quotes in the CUSTOMER's currency while
 * costing in the company's. The strings are passed through verbatim.
 *
 * It does not invent a revision number for edits that did not bump one. Changing a convenience
 * fee is a real edit and appears in the history, but it is not a revision, and calling it one
 * would make the number mean something different from what the customer was sent.
 */

/** One `Version` row as `/api/resource/Version` returns it. `data` is a JSON string. */
export interface WireVersion {
  name: string
  creation: string
  owner: string
  data: string
}

/**
 * What KIND of change this is, so the screen can show it as something other than a line of text.
 * The parser decides this — a component re-deriving it from the wording would drift the moment a
 * label changed.
 */
export type ChangeKind = 'added' | 'removed' | 'qty' | 'total' | 'status' | 'field'

export interface HistoryChange {
  kind: ChangeKind
  /** Human wording — `convenience_amount` is not what anyone calls it. */
  label: string
  from: string
  to: string
}

export interface HistoryEntry {
  id: string
  /** `"YYYY-MM-DD HH:mm:ss"`, site local time — not ISO, same as everything else on this wire. */
  at: string
  by: string
  /** The round this edit STARTED, when it bumped one; `null` for an edit that did not. */
  revision: number | null
  /** What changed, most significant first. */
  changes: HistoryChange[]
}

/**
 * Wire field names as a person would say them. Anything not listed falls back to the raw name
 * rather than a prettified guess — a wrong label on a money field is worse than an ugly one.
 */
const FIELD_LABELS: Record<string, string> = {
  convenience_amount: 'Convenience fee',
  management_commission: 'Broker commission',
  markup: 'Markup',
  sell_unit: 'Selling price',
  cost_unit: 'Cost',
  margin: 'Margin',
  uom: 'Unit',
  customer_currency: 'Customer currency',
  exchange_rate: 'Exchange rate',
  sent_to: 'Sent to',
  sent_at: 'Sent at',
  valid_till: 'Valid till',
  purchaser: 'Purchaser',
  sales_person: 'Sales person',
}

/** Fields whose movement is a consequence of another change, not a change anyone made. */
const DERIVED = new Set(['overall_margin', 'total_commission', 'profit_percentage', 'amount'])

/**
 * Fields that restate another one and would be listed twice.
 *
 * `net_total`/`total` mirror `grand_total`, and `unit_price` mirrors `sell_unit` — measured
 * 2026-08-09, a single price edit wrote BOTH as `AED 13.79 → AED 14.50`. Showing the same change
 * under two names reads as two changes.
 */
const REDUNDANT = new Set(['net_total', 'total', 'unit_price'])

interface VersionData {
  changed?: [string, unknown, unknown][]
  row_changed?: [string, number, string, [string, unknown, unknown][]][]
  added?: [string, Record<string, unknown>][]
  removed?: [string, Record<string, unknown>][]
}

const text = (v: unknown): string => (v === null || v === undefined || v === '' ? '—' : String(v))

function parse(row: WireVersion): VersionData {
  try {
    const parsed: unknown = JSON.parse(row.data || '{}')
    return typeof parsed === 'object' && parsed !== null ? (parsed as VersionData) : {}
  } catch {
    // A row we cannot read is not a reason to blank the panel — it becomes an entry with no
    // detail rather than an exception, and the rest of the history still renders.
    return {}
  }
}

/**
 * Frappe's audit rows as a readable history, newest first.
 *
 * Rows that changed nothing legible are DROPPED rather than shown as empty entries: a save that
 * only moved a derived figure is noise, and a history padded with blank rows stops being read.
 */
export function toHistory(rows: readonly WireVersion[]): HistoryEntry[] {
  const entries: HistoryEntry[] = []

  for (const row of rows) {
    const data = parse(row)
    const changes: HistoryChange[] = []
    let revision: number | null = null

    for (const [field, from, to] of data.changed ?? []) {
      if (field === 'revision') {
        const next = Number(to)
        revision = Number.isFinite(next) ? next : null
        continue // the round is the entry's headline, not one of its lines
      }
      if (field === 'status') {
        changes.push({ kind: 'status', label: 'Status', from: text(from), to: text(to) })
        continue
      }
      if (DERIVED.has(field) || REDUNDANT.has(field)) continue
      if (field === 'grand_total') {
        changes.push({ kind: 'total', label: 'Total', from: text(from), to: text(to) })
        continue
      }
      changes.push({ kind: 'field', label: FIELD_LABELS[field] ?? field, from: text(from), to: text(to) })
    }

    for (const [table, , , fields] of data.row_changed ?? []) {
      if (table !== 'items') continue
      for (const [field, from, to] of fields) {
        if (DERIVED.has(field) || REDUNDANT.has(field)) continue
        changes.push({
          kind: field === 'qty' ? 'qty' : field === 'sell_unit' ? 'total' : 'field',
          label: field === 'qty' ? 'Quantity' : (FIELD_LABELS[field] ?? `Line ${field}`),
          from: text(from),
          to: text(to),
        })
      }
    }

    for (const [table, added] of data.added ?? []) {
      if (table !== 'items') continue
      changes.push({ kind: 'added', label: 'Line added', from: '', to: text(added.item_code) })
    }
    for (const [table, removed] of data.removed ?? []) {
      if (table !== 'items') continue
      changes.push({ kind: 'removed', label: 'Line removed', from: text(removed.item_code), to: '' })
    }

    if (changes.length === 0 && revision === null) continue

    entries.push({ id: row.name, at: row.creation, by: row.owner, revision, changes })
  }

  return entries
}

/**
 * "4 minutes ago" / "2 hours ago" / "yesterday" / "12 Aug".
 *
 * `format.ts`'s `formatAgo` is day-granular ("today" / "N days ago"), which collapses a whole
 * afternoon's edits into one word — and this history routinely has several saves within minutes
 * of each other, so ordering is exactly what a reader needs from it.
 *
 * Frappe sends `"YYYY-MM-DD HH:mm:ss"` in SITE LOCAL time, not ISO-8601 UTC. Safari refuses that
 * format outright, so the space is replaced with `T` before parsing rather than left to each
 * browser's tolerance — a history that silently reads "Invalid Date" in one browser is worse than
 * one that shows the raw stamp.
 */
export function timeAgo(at: string, now: Date = new Date()): string {
  const parsed = new Date(at.trim().replace(' ', 'T'))
  const then = parsed.getTime()
  if (Number.isNaN(then)) return at // unparseable: show what the server said, never "Invalid Date"

  const seconds = Math.floor((now.getTime() - then) / 1000)
  if (seconds < 0) return 'just now' // clock skew between server and browser, not a future edit
  if (seconds < 60) return 'just now'

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`

  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`

  // Past a week, a date is easier to place than a count.
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/* ==============================================================================================
 * Grouping the history by what was actually SENT
 * ============================================================================================ */

/** One outgoing quotation email, from `Email Queue`. */
export interface SendRecord {
  id: string
  /** `"YYYY-MM-DD HH:mm:ss.ffffff"`, same format and clock as `Version.creation`. */
  at: string
  /** `Email Queue.status` — `Sent`, `Error`, `Not Sent`… */
  status: string
}

export interface SentRound {
  id: string
  sentAt: string
  /** False when the mail never left — an attempted send is not a sent quotation. */
  delivered: boolean
  /** The round the customer received, when a revision bump preceded this send. */
  revision: number | null
  /** What was different from the version sent before it. */
  changes: HistoryChange[]
}

/**
 * The history a customer would recognise: one entry per quotation actually emailed, carrying what
 * changed since the previous one.
 *
 * ---- why sends come from `Email Queue` ----
 *
 * Measured 2026-08-09: `Version` NEVER records a send. `status` moves `Quoted -> Draft` when a
 * revision reopens the document, but the `Draft -> Quoted` on send is written outside Frappe's
 * versioning (`db_set` rather than `save`), so the audit trail has no trace of it. `Email Queue`
 * does — three rows for `QTN-2026-00004`, each with the moment it went out and whether it
 * actually left.
 *
 * ---- why the raw list is not what anyone wanted ----
 *
 * Every keystroke that reaches the server is a `Version`. Listing all of them answers "what has
 * been touched", when the question is "what did the customer get, and how did it differ from last
 * time". A round is therefore the unit: the send, and everything that changed since the previous
 * send.
 *
 * Changes made AFTER the last send are returned separately as `unsent`, not hidden. Dropping them
 * would let the panel imply the customer holds the current figures when they do not — the one
 * thing a history must never do.
 */
export function toSentRounds(
  entries: readonly HistoryEntry[],
  sends: readonly SendRecord[],
): { rounds: SentRound[]; unsent: HistoryChange[] } {
  // Both arrive newest-first; walking forwards in time is what makes "since the previous send"
  // expressible. Timestamps share one fixed-width format and one clock, so string order IS time
  // order — no parsing, and no timezone to get wrong.
  const asc = [...entries].sort((a, b) => a.at.localeCompare(b.at))
  const sendsAsc = [...sends].sort((a, b) => a.at.localeCompare(b.at))

  const rounds: SentRound[] = []
  let cursor = 0

  for (const send of sendsAsc) {
    const changes: HistoryChange[] = []
    let revision: number | null = null

    while (cursor < asc.length && asc[cursor].at.localeCompare(send.at) <= 0) {
      const entry = asc[cursor]
      changes.push(...entry.changes)
      if (entry.revision !== null) revision = entry.revision
      cursor++
    }

    rounds.push({
      id: send.id,
      sentAt: send.at,
      delivered: send.status === 'Sent',
      revision,
      changes,
    })
  }

  // Anything left is later than every send: edited but not yet in a customer's hands.
  const unsent = asc.slice(cursor).flatMap((e) => e.changes)

  return { rounds: rounds.reverse(), unsent }
}

